import ColumnDatePicker from 'components/ColumnDatePicker';
import CheckboxGroup, {
  CheckboxGroupOption
} from 'lib/components/Checkbox/CheckboxGroup';
import { Label } from 'lib/components/Label';
import moment from 'moment';
import React from 'react';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import {
  ALL_INVOICE_CREATION_DATES,
  ALL_INVOICES_STATUS,
  CreationDatesOptionValue,
  CUSTOM_INVOICE_CREATION_DATES,
  InvoiceTableFilter,
  InvoiceTableStatus,
  INVOICE_CREATION_DATE_OPTIONS,
  INVOICE_STATUSE_OPTIONS,
  LAST_MONTH_INVOICE_CREATION_DATES,
  THIS_MONTH_INVOICE_CREATION_DATES
} from './invoiceTableSettingsUtils';

type InvoicesTableFilterDialogProps = {
  setUpdatedInvoiceFilter: (filter: InvoiceTableFilter) => void;
  updatedInvoiceTableFilter: InvoiceTableFilter;
};

export default function InvoicesTableFilterDialog({
  setUpdatedInvoiceFilter,
  updatedInvoiceTableFilter
}: InvoicesTableFilterDialogProps) {
  const isCustomDateRange = ![
    ALL_INVOICE_CREATION_DATES,
    THIS_MONTH_INVOICE_CREATION_DATES,
    LAST_MONTH_INVOICE_CREATION_DATES,
    {}
  ].includes(updatedInvoiceTableFilter.dateRange);

  // parse the date range into a more easily typed format
  const currentDateRange = isCustomDateRange
    ? updatedInvoiceTableFilter.dateRange
    : null;

  return (
    <>
      <p className="uppercase text-column-gray-300 text-xs font-bold">
        Filter by
      </p>

      {/* Control what subset of invoices by status we're looking at */}
      <CheckboxGroup<InvoiceTableStatus>
        id="invoice-status-filter"
        labelText="Status"
        stacked
        options={INVOICE_STATUSE_OPTIONS.map(
          ({ label, value }): CheckboxGroupOption<InvoiceTableStatus> => ({
            id: value as InvoiceTableStatus,
            labelText: label,
            checked:
              value === ALL_INVOICES_STATUS
                ? updatedInvoiceTableFilter.invoiceTableStatuses.includes(
                    ALL_INVOICES_STATUS
                  )
                : updatedInvoiceTableFilter.invoiceTableStatuses.includes(
                    value as InvoiceTableStatus
                  )
          })
        )}
        onChange={(newOptions, changedOption) => {
          let newStatuses: InvoiceTableStatus[];
          if (
            changedOption.id === ALL_INVOICES_STATUS &&
            changedOption.checked
          ) {
            newStatuses = [ALL_INVOICES_STATUS];
          } else {
            const specificSelected = newOptions
              .filter(opt => opt.id !== ALL_INVOICES_STATUS && opt.checked)
              .map(opt => opt.id);
            newStatuses =
              specificSelected.length > 0
                ? specificSelected
                : [ALL_INVOICES_STATUS];
          }
          setUpdatedInvoiceFilter({
            ...updatedInvoiceTableFilter,
            invoiceTableStatuses: newStatuses
          });
        }}
      />

      {/* Control the publication dates visible in the table */}
      <ColumnSelect
        id="date-created"
        labelText="Date Created"
        size="sm"
        options={INVOICE_CREATION_DATE_OPTIONS}
        value={
          isCustomDateRange
            ? CUSTOM_INVOICE_CREATION_DATES
            : updatedInvoiceTableFilter.dateRange.toString()
        }
        onChange={newValue => {
          if (newValue === CUSTOM_INVOICE_CREATION_DATES) {
            setUpdatedInvoiceFilter({
              ...updatedInvoiceTableFilter,
              dateRange: {
                start: moment().subtract(1, 'month').toDate(),
                end: moment().toDate()
              }
            });
          } else {
            setUpdatedInvoiceFilter({
              ...updatedInvoiceTableFilter,
              dateRange: newValue as CreationDatesOptionValue
            });
          }
        }}
      />

      {/* Filter on a custom date range */}
      {currentDateRange && typeof currentDateRange !== 'string' && (
        <div>
          {/* TODO: Update ColumnDatePicker to support InputAccessories */}
          <Label id="date-filter-label">Choose a date range</Label>
          <div className="flex flex-row gap-2">
            <ColumnDatePicker
              className="w-1/2"
              format="MMM dd"
              momentFormat="MMM DD"
              placeholderText="Start date"
              value={currentDateRange?.start}
              onChange={newDate =>
                newDate &&
                setUpdatedInvoiceFilter({
                  ...updatedInvoiceTableFilter,
                  dateRange: {
                    ...currentDateRange,
                    start: newDate
                  }
                })
              }
              shouldDisableDate={date =>
                !date || moment(date).isAfter(currentDateRange.end)
              }
            />
            <ColumnDatePicker
              className="w-1/2"
              format="MMM dd"
              momentFormat="MMM DD"
              placeholderText="End date"
              value={currentDateRange.end}
              onChange={newDate =>
                newDate &&
                setUpdatedInvoiceFilter({
                  ...updatedInvoiceTableFilter,
                  dateRange: {
                    ...currentDateRange,
                    end: newDate
                  }
                })
              }
              shouldDisableDate={date => !date || moment().isBefore(date)}
            />
          </div>
        </div>
      )}
    </>
  );
}
